if (!customElements.get('cart-recommendations')) {
  class CartRecommendations extends HTMLElement {
    static SELECTORS = {
      staticContainer: '.cart-recommendations__items--static',
      dynamicContainer: '.cart-recommendations__items--dynamic',
      productCard: 'product-card',
    };

    static CLASSES = {
      hidden: 'hidden',
      fadeIn: 'u-fade-in',
      loaded: 'cart-recommendations--loaded',
    };

    abortController = null;
    elements = {
      staticContainer: null,
      dynamicContainer: null,
    };

    constructor() {
      super();

      this.debouncedOnChange = debounce(() => {
        this.onChange();
      }, 500);
    }

    connectedCallback() {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartInterceptor.update, () => {
        this.debouncedOnChange();
      });

      this.cartChangedUnsubscriber = subscribe(PUB_SUB_EVENTS.cartInterceptor.change, () => {
        this.debouncedOnChange();
      });
      
      this.cartClearUnsubscriber = subscribe(PUB_SUB_EVENTS.cartInterceptor.clear, () => {
        this.debouncedOnChange();
      });

      // predefined recommendations are rendred on server.
      if (this.mode() === 'shopify') {
        this.cartDrawerOpenUnsubscriber = subscribe(PUB_SUB_EVENTS.cartDrawer.open, () => {
          this.debouncedOnChange();
        });
      }

      if (this.mode() === 'shopify') {
        this.setElements();
      } else {
        this.applyRecommendationsVisibility(this.isCartEmptyFromDataset());
      }
    }

    mode() {
      return this.dataset.mode;
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.cartUpdateUnsubscriber?.();
      this.cartChangedUnsubscriber?.();
      this.cartClearUnsubscriber?.();
      this.cartDrawerOpenUnsubscriber?.();
    }

    setElements() {
      this.elements = {
        staticContainer: this.querySelector(CartRecommendations.SELECTORS.staticContainer),
        dynamicContainer: this.querySelector(CartRecommendations.SELECTORS.dynamicContainer),
      };
    }

    reconcileRecommendations(sourceItemsContainer, targetItemsContainer) {
      const sourceCards = [...sourceItemsContainer.querySelectorAll(CartRecommendations.SELECTORS.productCard)];
      const existingCards = [...targetItemsContainer.querySelectorAll(CartRecommendations.SELECTORS.productCard)];

      const newHandles = sourceCards.map((c) => c.dataset.handle);
      const existingHandles = existingCards.map((c) => c.dataset.handle);

      // Skip re-render if cards are identical in order
      if (newHandles.length === existingHandles.length && newHandles.every((h, i) => h === existingHandles[i])) return;

      existingCards.forEach((card) => card.remove());

      sourceCards.forEach((card, index) => {
        const clone = card.cloneNode(true);
        clone.classList.add(CartRecommendations.CLASSES.fadeIn);
        clone.style.setProperty('--animation-fade-in-delay', `${index * 0.1}s`);
        targetItemsContainer.appendChild(clone);
      });
    }

    onChange() {
      this.refreshRecommendations();
    }

    isCartEmptyFromDataset() {
      return this.dataset.cartEmpty === 'true';
    }

    isValidRecommendationsUrl(url) {
      if (!url) return false;

      const productIdMatch = String(url).match(/[?&]product_id=([^&]*)/);
      if (!productIdMatch) return true;

      return productIdMatch[1] !== '';
    }

    applyRecommendationsVisibility(isCartEmpty) {
      this.setElements();

      const staticHasProducts = !!this.elements.staticContainer?.querySelector(CartRecommendations.SELECTORS.productCard);
      const dynamicHasProducts = !!this.elements.dynamicContainer?.querySelector(CartRecommendations.SELECTORS.productCard);

      const shouldShowDynamic = !isCartEmpty && dynamicHasProducts;
      const shouldShowStatic = isCartEmpty ? staticHasProducts : !shouldShowDynamic && staticHasProducts;

      if (this.elements.staticContainer) {
        this.elements.staticContainer.classList.toggle(CartRecommendations.CLASSES.hidden, !shouldShowStatic);
      }

      if (this.elements.dynamicContainer) {
        this.elements.dynamicContainer.classList.toggle(CartRecommendations.CLASSES.hidden, !shouldShowDynamic);
      }

      if (!staticHasProducts && !dynamicHasProducts) {
        this.remove();
      }
    }

    clearDynamicRecommendations() {
      this.setElements();
      this.elements.dynamicContainer?.querySelectorAll(CartRecommendations.SELECTORS.productCard).forEach((card) => card.remove());
    }

    parseRecommendationsResponse(text, fallbackIsCartEmpty) {
      const html = document.createElement('div');
      html.innerHTML = text;

      const responseRecommendations = html.querySelector('cart-recommendations');
      const responseCartEmpty = responseRecommendations?.dataset?.cartEmpty;
      const responseUrl = responseRecommendations?.dataset?.url;
      const responseReferenceCartItemKey = responseRecommendations?.dataset?.referenceCartItemKey;
      const effectiveIsCartEmpty = responseCartEmpty ? responseCartEmpty === 'true' : fallbackIsCartEmpty;
      const sourceItemsContainer = html.querySelector(CartRecommendations.SELECTORS.dynamicContainer);
      const sourceStaticContainer = html.querySelector(CartRecommendations.SELECTORS.staticContainer);

      return {
        effectiveIsCartEmpty,
        responseUrl,
        responseReferenceCartItemKey,
        sourceItemsContainer,
        sourceStaticContainer,
      };
    }

    applyRecommendationsResponse(parsedResponse) {
      this.setElements();
      this.dataset.cartEmpty = String(parsedResponse.effectiveIsCartEmpty);

      if (this.isValidRecommendationsUrl(parsedResponse.responseUrl)) {
        this.dataset.url = parsedResponse.responseUrl;
      }

      if (parsedResponse.responseReferenceCartItemKey) {
        this.dataset.referenceCartItemKey = parsedResponse.responseReferenceCartItemKey;
      }

      const targetStaticContainer = this.elements.staticContainer;
      if (parsedResponse.sourceStaticContainer && targetStaticContainer) {
        this.reconcileRecommendations(parsedResponse.sourceStaticContainer, targetStaticContainer);
      }

      const targetItemsContainer = this.elements.dynamicContainer;

      if (parsedResponse.sourceItemsContainer?.innerHTML.trim().length && targetItemsContainer) {
        this.reconcileRecommendations(parsedResponse.sourceItemsContainer, targetItemsContainer);
      }

      this.applyRecommendationsVisibility(parsedResponse.effectiveIsCartEmpty);

      if (parsedResponse.sourceItemsContainer?.querySelector(CartRecommendations.SELECTORS.productCard)) {
        this.classList.add(CartRecommendations.CLASSES.loaded);
      }
    }

    refreshRecommendations() {
      const isCartEmpty = this.isCartEmptyFromDataset();

      if (isCartEmpty) {
        this.clearDynamicRecommendations();
        this.applyRecommendationsVisibility(true);
        return;
      }

      const requestUrl = this.dataset.url;
      if (!this.isValidRecommendationsUrl(requestUrl)) {
        this.clearDynamicRecommendations();
        this.applyRecommendationsVisibility(false);
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();

      fetch(requestUrl, { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((text) => this.parseRecommendationsResponse(text, isCartEmpty))
        .then((parsedResponse) => {
          this.applyRecommendationsResponse(parsedResponse);
        })
        .catch((e) => {
          if (e?.name === 'AbortError') return;
          console.error(e);
        });
    }
  }
  customElements.define('cart-recommendations', CartRecommendations);
}
